import { Button, ModalActions, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { baseUrl, postKmsData, translate, useGuessedTimeZone } from "@kaltura/mediaspace-shared-utils";
import { FormProvider, useForm } from "react-hook-form";
import { useState } from "react";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { StyledCreateRoomModal } from "./styledComponents";
import CreateRoomFormContainer from "./form/CreateRoomFormContainer";

export interface CreateRoomModalProps {
    /**
     * is the modal currently open
     */
    open: boolean;

    /**
     * cancel button callback
     */
    onCancel: () => void;
}

export function CreateRoomModal(props: CreateRoomModalProps) {
    const { open, onCancel } = props;

    const [processing, setProcessing] = useState(false);

    const guessedTz = useGuessedTimeZone();

    const { showToast } = useToastsContext();
    const useFormHookMethods = useForm({
        mode: "onChange",
        defaultValues: {
            name: "",
            tz: guessedTz,
        },
    });

    const sendButtonAnalytics = useButtonAnalytics();

    const handleSubmit = async (data: { [key: string]: string | boolean }) => {
        setProcessing(true);

        try {
            const result = await postKmsData(`${baseUrl}/meetingentry/entry/create-room`, data);
            if (result.success && result.redirectUrl) {
                // redirect to the new room page on success
                sendButtonAnalytics("Create Room modal - Create", ButtonClickAnalyticsType.CREATE);
                window.location.href = result.redirectUrl;
            }
            if (!result.success) {
                setProcessing(false);
                if (result.field && result.field === "name") {
                    // show name field error message, located below the input
                    useFormHookMethods.setError("name", { type: "manual", message: result.errorMessage });
                }
                else {
                    // show toast in case of other errors
                    showToast({ severity: "error", message: result.errorMessage });
                }
            }
        }
        catch (e) {
            setProcessing(false);
            showToast({ severity: "error", message: translate("Failed to create room") });
        }
    };

    const handleCancel = () => {
        sendButtonAnalytics("Create Room modal - Cancel", ButtonClickAnalyticsType.CLOSE);
        onCancel();
    };

    const baseClassName = "kms-ds-create-room-modal";

    return (
        <StyledCreateRoomModal open={open} classes={{ paper: `${baseClassName}` }}>
            <ModalTitle className={`${baseClassName}-title`}>{translate("Create a Room")}</ModalTitle>
            <ModalContent className={`${baseClassName}-content`}>
                <FormProvider {...useFormHookMethods}>
                    {open && <CreateRoomFormContainer onSubmit={handleSubmit} />}
                </FormProvider>
            </ModalContent>
            <ModalActions className={`${baseClassName}-actions`}>
                <Button
                    onClick={handleCancel}
                    variant={"borderless"}
                    color={"secondary"}
                    className={`${baseClassName}-actions-cancel`}
                >
                    {translate("Cancel")}
                </Button>
                <Button
                    onClick={useFormHookMethods.handleSubmit(handleSubmit)}
                    type={"submit"}
                    loading={processing}
                    disabled={processing || !useFormHookMethods.formState.isValid}
                    className={`${baseClassName}-actions-submit`}
                >
                    {translate("Create")}
                </Button>
            </ModalActions>
        </StyledCreateRoomModal>
    );
}

export default CreateRoomModal;
